import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  AlertTriangle, Search, Plus, 
  FileText, GitBranch, ChevronRight, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useApp } from '../context/AppContext';
import { useTrace } from '../context/TraceContext';
import { useQualityEvents } from '../context/QualityEventsContext';
import { useFilters } from '../context/FilterContext';
import TraceLabel from '../components/trace/TraceLabel';
import { areas, formatDate } from '../data/mockData';
import DeviationCreateDialog from '../components/quality-events/DeviationCreateDialog';
import InvestigationForm from '../components/quality-events/InvestigationForm';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { toast } from 'sonner';

const DeviationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pidTraceMode } = useTrace();
  const { isReadOnly, currentRole } = useApp();
  const { plant, area, timeRange } = useFilters();
  const {
    deviations,
    deviationCounts,
    filterDeviations,
    getDeviationById,
    startInvestigation,
    closeDeviation,
    permissions,
    can
  } = useQualityEvents();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDeviation, setSelectedDeviation] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Load deviation from URL param
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const dev = getDeviationById(id);
      if (dev) {
        setSelectedDeviation(dev);
      }
    }
  }, [searchParams, getDeviationById]);

  // Update selected deviation when deviations change
  useEffect(() => {
    if (selectedDeviation) {
      const updated = getDeviationById(selectedDeviation.id);
      if (updated) {
        setSelectedDeviation(updated);
      }
    }
  }, [deviations, selectedDeviation, getDeviationById]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-50 text-red-700 border-red-200';
      case 'Major': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Minor': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-50 text-red-700 border-red-200';
      case 'Under Investigation': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'CAPA Assigned': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Closed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getAreaName = (areaId) => {
    const areaObj = areas.find(a => a.id === areaId);
    return areaObj ? areaObj.name : areaId;
  };

  // Filter deviations using context
  const filteredDeviations = useMemo(() => {
    // Debug: log deviations count
    if (deviations.length === 0) {
      console.warn('⚠️ Deviations array is empty! Total:', deviations.length);
    } else {
      console.log('✅ Deviations loaded:', deviations.length, 'items');
    }
    
    const filtered = filterDeviations({
      plant,
      area,
      timeRange,
      status: statusFilter,
      severity: severityFilter,
      search: searchQuery
    });
    
    // Debug: log if no results
    if (filtered.length === 0 && deviations.length > 0) {
      console.warn('⚠️ Filtered deviations is empty but deviations array has', deviations.length, 'items');
      console.log('Filters:', { plant, area, timeRange, statusFilter, severityFilter, searchQuery });
      console.log('Sample deviation:', deviations[0]);
    }
    return filtered;
  }, [filterDeviations, plant, area, timeRange, statusFilter, severityFilter, searchQuery, deviations]);

  const handleStartInvestigation = () => {
    if (!selectedDeviation) return;
    try {
      startInvestigation(selectedDeviation.id);
      toast.success('Investigation started', {
        description: 'You can now fill in the investigation details'
      });
      setActiveTab('investigation');
    } catch (error) {
      toast.error('Failed to start investigation', {
        description: error.message
      });
    }
  };

  const handleCloseDeviation = () => {
    if (!selectedDeviation) return;
    const closureNotes = prompt('Enter closure notes:');
    if (!closureNotes) return;

    try {
      closeDeviation(selectedDeviation.id, closureNotes);
      toast.success('Deviation closed');
    } catch (error) {
      toast.error('Cannot close deviation', {
        description: error.message
      });
    }
  };

  const handleDeviationCreated = (newDeviation) => {
    setSelectedDeviation(newDeviation);
    navigate(`/deviations?id=${newDeviation.id}`);
  };

  const DeviationRow = ({ deviation }) => (
    <TableRow 
      className={`cursor-pointer hover:bg-slate-50 ${selectedDeviation?.id === deviation.id ? 'bg-slate-100' : ''}`}
      onClick={() => {
        setSelectedDeviation(deviation);
        navigate(`/deviations?id=${deviation.id}`);
      }}
      data-testid={`deviation-row-${deviation.id}`}
    >
      <TableCell className="font-mono text-xs text-slate-500">{deviation.id}</TableCell>
      <TableCell>
        <div className="max-w-xs">
          <p className="text-sm text-slate-700 truncate font-medium">{deviation.title || deviation.description}</p>
          <p className="text-xs text-slate-500">{getAreaName(deviation.area)}</p>
        </div>
      </TableCell>
      <TableCell>
        <Badge className={`${getSeverityColor(deviation.severity)} text-xs`}>{deviation.severity}</Badge>
      </TableCell>
      <TableCell>
        <Badge className={`${getStatusColor(deviation.status)} text-xs`}>{deviation.status}</Badge>
      </TableCell>
      <TableCell className="text-sm text-slate-600">{formatDate(deviation.reportedAt)}</TableCell>
      <TableCell className="text-sm text-slate-600">{deviation.reportedBy}</TableCell>
      <TableCell>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </TableCell>
    </TableRow>
  );

  const DeviationDetail = ({ deviation }) => {
    const { getCAPAById } = useQualityEvents();
    const linkedCAPA = deviation.capaId ? getCAPAById(deviation.capaId) : null;

    return (
      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="investigation">Investigation</TabsTrigger>
            <TabsTrigger value="capa">Linked CAPA</TabsTrigger>
            <TabsTrigger value="evidence">Evidence</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-mono mb-1">{deviation.id}</p>
                <h3 className="text-lg font-medium text-slate-900">{deviation.title || deviation.description}</h3>
              </div>
              <div className="flex gap-2">
                <Badge className={`${getSeverityColor(deviation.severity)}`}>{deviation.severity}</Badge>
                <Badge className={`${getStatusColor(deviation.status)}`}>{deviation.status}</Badge>
              </div>
            </div>

            <Separator className="bg-slate-200" />

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Area</p>
                <p className="text-sm text-slate-700 font-medium">{getAreaName(deviation.area)}</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Reported By</p>
                <p className="text-sm text-slate-700 font-medium">{deviation.reportedBy}</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Reported At</p>
                <p className="text-sm text-slate-700">{formatDate(deviation.reportedAt)}</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Category</p>
                <p className="text-sm text-slate-700">{deviation.category || 'N/A'}</p>
              </div>
              {deviation.batch && (
                <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Linked Batch</p>
                  <p className="text-sm text-slate-700 font-mono">{deviation.batch}</p>
                </div>
              )}
              {deviation.equipment && (
                <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Equipment</p>
                  <p className="text-sm text-slate-700 font-mono">{deviation.equipment}</p>
                </div>
              )}
              {deviation.room && (
                <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Room</p>
                  <p className="text-sm text-slate-700 font-mono">{deviation.room}</p>
                </div>
              )}
              {deviation.capaId && (
                <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">CAPA</p>
                  <Link to={`/capa?id=${deviation.capaId}`} className="text-sm text-[#fd4816] hover:underline font-mono">
                    {deviation.capaId}
                  </Link>
                </div>
              )}
            </div>

            {deviation.description && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Description</p>
                <p className="text-sm text-slate-700">{deviation.description}</p>
              </div>
            )}

            {deviation.immediateAction && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Immediate Action</p>
                <p className="text-sm text-slate-700">{deviation.immediateAction}</p>
              </div>
            )}

            {/* Actions */}
            {!isReadOnly && deviation.status !== 'Closed' && (
              <div className="flex gap-2 pt-2">
                {deviation.status === 'Open' && can('deviation:startInvestigation') && (
                  <Button 
                    className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1" 
                    onClick={handleStartInvestigation}
                    data-testid="start-investigation"
                  >
                    <GitBranch className="h-4 w-4 mr-2" />
                    Start Investigation
                  </Button>
                )}
                {deviation.status === 'Closed' && can('deviation:close') && (
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleCloseDeviation}
                  >
                    Reopen Deviation
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="investigation" className="space-y-4 mt-4">
            {deviation.investigation ? (
              <InvestigationForm 
                deviation={deviation}
                onInvestigationSaved={() => {
                  const updated = getDeviationById(deviation.id);
                  if (updated) setSelectedDeviation(updated);
                }}
                onCAPACreated={(capa) => {
                  navigate(`/capa?id=${capa.id}`);
                }}
              />
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Investigation not started yet.</p>
                {deviation.status === 'Open' && can('deviation:startInvestigation') && (
                  <Button 
                    className="mt-4 bg-[#fd4816] hover:bg-[#ea380c] text-white"
                    onClick={handleStartInvestigation}
                  >
                    Start Investigation
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="capa" className="space-y-4 mt-4">
            {linkedCAPA ? (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-md p-4 border border-slate-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs text-slate-500 font-mono mb-1">{linkedCAPA.id}</p>
                      <h4 className="text-sm font-medium text-slate-900">{linkedCAPA.title}</h4>
                    </div>
                    <Badge className={getStatusColor(linkedCAPA.status)}>{linkedCAPA.status}</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate(`/capa?id=${linkedCAPA.id}`)}
                  >
                    View CAPA Details
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No CAPA linked to this deviation.</p>
                {deviation.investigation?.capaRequired === true && (
                  <p className="text-xs mt-2">Create CAPA from Investigation tab</p>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="evidence" className="space-y-4 mt-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Attachments</p>
              {deviation.attachments && deviation.attachments.length > 0 ? (
                <div className="space-y-2">
                  {deviation.attachments.map((att, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-md p-3 border border-slate-200 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700">{att.name || 'Attachment'}</p>
                        <p className="text-xs text-slate-500">{att.addedAt ? formatDate(att.addedAt) : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No attachments</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="deviations-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Deviations
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage deviation investigations and root cause analysis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-red-50 text-red-700 border border-red-200">
            {deviationCounts.open} Open
          </Badge>
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            {deviationCounts.underInvestigation} Under Investigation
          </Badge>
          {/* <ReportExportButton 
            title="Deviations Report"
            subtitle="Deviation investigations and root cause analysis"
          /> */}
          {can('deviation:create') && (
            <Button 
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white" 
              onClick={() => setCreateDialogOpen(true)}
              data-testid="new-deviation"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Deviation
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List Panel */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <ReportSection id="deviations-filters" title="Filters" description="Search and filter deviations">
            <Card className={`bg-white border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.4" req="SM-DEV-001" />
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search deviations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white border-slate-200 text-slate-700"
                    data-testid="deviation-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px] bg-white border-slate-200 text-slate-700">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all" className="text-slate-700">All Status</SelectItem>
                    <SelectItem value="Open" className="text-slate-700">Open</SelectItem>
                    <SelectItem value="Under Investigation" className="text-slate-700">Under Investigation</SelectItem>
                    <SelectItem value="CAPA Assigned" className="text-slate-700">CAPA Assigned</SelectItem>
                    <SelectItem value="Closed" className="text-slate-700">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-[150px] bg-white border-slate-200 text-slate-700">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all" className="text-slate-700">All Severity</SelectItem>
                    <SelectItem value="Critical" className="text-slate-700">Critical</SelectItem>
                    <SelectItem value="Major" className="text-slate-700">Major</SelectItem>
                    <SelectItem value="Minor" className="text-slate-700">Minor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          </ReportSection>

          {/* Deviations Table */}
          <ReportSection id="deviations-list" title="Deviations List" description="All reported deviations">
            <Card className={`bg-white border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.4" req="SM-DI-001" />
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Description</TableHead>
                  <TableHead className="text-slate-600">Severity</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Reported</TableHead>
                  <TableHead className="text-slate-600">Reporter</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeviations.map(deviation => (
                  <DeviationRow key={deviation.id} deviation={deviation} />
                ))}
                {filteredDeviations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      {can('deviation:create') ? (
                        <div className="space-y-2">
                          <p>No deviations found</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCreateDialogOpen(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create New Deviation
                          </Button>
                        </div>
                      ) : (
                        <p>No deviations found</p>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          <Card className={`bg-white border-slate-200 shadow-sm sticky top-20 relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §10" req="SM-DI-001" />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Deviation Detail</CardTitle>
                {selectedDeviation && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedDeviation(null);
                      navigate('/deviations');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[calc(100vh-280px)]">
                {selectedDeviation ? (
                  <DeviationDetail deviation={selectedDeviation} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Select a deviation to view details</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <DeviationCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleDeviationCreated}
      />
    </div>
  );
};

export default DeviationsPage;
